import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CATEGORY_OPTIONS } from "./fileUtils";

const splitName = (name) => {
  const i = (name || "").lastIndexOf(".");
  return i > 0 ? [name.slice(0, i), name.slice(i)] : [name || "", ""];
};

export function FileEditDialog({ file, onClose, onSaved }) {
  const [base, ext] = splitName(file.filename);
  const [name, setName] = useState(base);
  const [category, setCategory] = useState(file.category || "other");
  const [docDate, setDocDate] = useState(file.document_date || "");
  const [description, setDescription] = useState(file.description || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) { toast.error("File name cannot be empty"); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/health-record/attachments/${file.id}`, {
        filename: `${name.trim()}${ext}`, category, description, document_date: docDate || "",
      });
      toast.success("File details saved");
      onSaved?.(r.data);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="file-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit file details</DialogTitle>
          <DialogDescription>Changes are recorded with your name and the time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">File name</Label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="file-edit-name" />
              {ext && <span className="text-sm text-zinc-500 font-mono" data-testid="file-edit-ext">{ext}</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                      data-testid="file-edit-category">
                {CATEGORY_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Document date</Label>
              <Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} className="mt-1"
                     data-testid="file-edit-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                      data-testid="file-edit-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="file-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="file-edit-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
